import React, { useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import type { Placement } from '@popperjs/core';
import { useOnClickOutside } from 'src/hooks/use-on-click-outside';
import './revamp-tooltip.scss';

type Offset = [number, number];

type ToolTipProps = {
  children: React.ReactElement;
  content: string | React.ReactElement;
  placement?: Placement;
  onClick?: () => void;
  parentRef: HTMLDivElement | null;
  offset?: Offset;
};

const RevampToolTip = ({
  children,
  content,
  placement = 'top-start',
  onClick,
  parentRef,
  offset,
}: ToolTipProps) => {
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [showPopper, setShowPopper] = useState(false);
  const refElem = useRef<HTMLDivElement>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: offset ? offset : [0, 20] } },
      {
        name: 'preventOverflow',
        options: {
          boundary: parentRef ?? undefined,
        },
      },
    ],
    placement,
  });

  function handleClick(event) {
    event.stopPropagation();
    if (showPopper) {
      popperElement?.removeAttribute('data-show');
      setShowPopper(false);
      return;
    }
    if (onClick) onClick();
    setShowPopper(true);
    popperElement?.setAttribute('data-show', '');
  }

  function closePopper() {
    popperElement?.removeAttribute('data-show');
    setShowPopper(false);
  }

  useOnClickOutside<HTMLDivElement | null>(refElem, closePopper);

  return (
    <>
      <div className="children-container-ref" ref={refElem}>
        <span className="children-container" ref={setReferenceElement} onClick={handleClick}>
          {children}
        </span>
      </div>

      <div
        className={'revamp-tooltip'}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        {content}
        <div id="arrow" data-popper-arrow="" className="arrow" ref={setArrowElement} />
      </div>
    </>
  );
};

export default RevampToolTip;
